import { apiRequest } from "../../api/client";
import { getApiErrorMessage } from "../../utils/apiError";
import { normalizeListPayload } from "../../utils/pagination";

const query = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") search.set(key, value);
  });
  const value = search.toString();
  return value ? `?${value}` : "";
};
const data = (payload) => payload?.data ?? payload;
const json = (method, body) => ({ method, body: JSON.stringify(body) });

export const makeSlug = (value = "") => value.toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
export const formatArticleDate = (value, { bilingual = false } = {}) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const english = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  if (!bilingual) return english;
  return `${english} · ${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
};
export const normalizePaginated = (payload, params = {}) =>
  normalizeListPayload(payload, { itemKeys: ["articles", "categories", "tags", "items", "media", "blocks"], perPage: params.per_page });
export { getApiErrorMessage };

export const getArticles = (params = {}) =>
  apiRequest(
    `/articles${query({
      page: 1,
      per_page: 20,
      sort_by: "created_at",
      sort_order: "desc",
      ...params,
    })}`
  );
export const getArticle = async(id) => data(await apiRequest(`/articles/${id}`));
export const createArticle = async(payload) => data(await apiRequest('/articles',json('POST',payload)));
export const updateArticle = async(id,payload) => data(await apiRequest(`/articles/${id}`,json('PUT',payload)));
export const deleteArticle = (id) => apiRequest(`/articles/${id}`,{method:'DELETE'});
export const getArticleCategories = (params = {}) =>
  apiRequest(
    `/article-categories${query({
      page: 1,
      per_page: 50,
      sort_by: "sort_order",
      sort_order: "desc",
      ...params,
    })}`
  );
export const getArticleCategory = async(id) => data(await apiRequest(`/article-categories/${id}`));
export const createArticleCategory = async(payload) => data(await apiRequest('/article-categories',json('POST',payload)));
export const updateArticleCategory = async(id,payload) => data(await apiRequest(`/article-categories/${id}`,json('PUT',payload)));
export const deleteArticleCategory = (id) => apiRequest(`/article-categories/${id}`,{method:'DELETE'});

export const getCategoryParentId = (category) => category?.parent_id ?? category?.parent?.id ?? null;

export const isSubcategory = (category) => getCategoryParentId(category) != null;

export const splitArticleCategories = (items = []) => {
  const list = Array.isArray(items) ? items : [];
  const seenChildIds = new Set();
  const childrenByParent = new Map();
  const parents = [];

  const addChild = (parentId, child) => {
    const key = Number(parentId);
    if (!Number.isFinite(key) || child?.id == null) return;
    const marker = `${key}:${Number(child.id)}`;
    if (seenChildIds.has(marker)) return;
    seenChildIds.add(marker);
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(child);
  };

  list.forEach((item) => {
    const parentId = getCategoryParentId(item);
    if (parentId == null) parents.push(item);
    else addChild(parentId, item);
    (item.children || []).forEach((child) => addChild(item.id, child));
  });

  return { parents, childrenByParent };
};

export const flattenArticleCategoryRows = (items = []) => {
  const { parents, childrenByParent } = splitArticleCategories(items);
  const rows = [];
  const listed = new Set();

  parents.forEach((parent) => {
    rows.push({ ...parent, _depth: 0 });
    listed.add(Number(parent.id));
    (childrenByParent.get(Number(parent.id)) || []).forEach((child) => {
      rows.push({
        ...child,
        parent_id: child.parent_id ?? parent.id,
        parent: child.parent || { id: parent.id, name: parent.name },
        _depth: 1,
      });
      listed.add(Number(child.id));
    });
  });

  listOrphanSubcategories(items, listed).forEach((item) => {
    rows.push({ ...item, _depth: 1 });
  });

  return rows;
};

const listOrphanSubcategories = (items, listed) =>
  (Array.isArray(items) ? items : []).filter((item) => {
    if (listed.has(Number(item.id))) return false;
    return isSubcategory(item);
  });

export const assignedArticleCategories = (article) => {
  const items = [];
  const push = (item) => {
    if (!item || item.id == null) return;
    items.push(item);
    if (item.parent) items.push(item.parent);
  };
  push(article?.category);
  push(article?.subcategory);
  (article?.categories || []).forEach(push);
  return items;
};

export const mergeArticleCategoryLists = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((item) => {
    if (item?.id == null) return;
    const key = Number(item.id);
    byId.set(key, { ...(byId.get(key) || {}), ...item });
  });
  return [...byId.values()];
};

const firstAssignedCategory = (article, predicate) =>
  assignedArticleCategories(article).find(predicate);

export const getArticleCategoryId = (article) => {
  if (article?.category_id != null && article?.subcategory_id != null) return article.category_id;
  const parent = firstAssignedCategory(article, (item) => !isSubcategory(item));
  if (parent) return parent.id;
  const child = firstAssignedCategory(article, isSubcategory);
  if (child) return getCategoryParentId(child);
  if (article?.category_id == null) return null;
  const match = firstAssignedCategory(article, (item) => Number(item.id) === Number(article.category_id));
  if (match && isSubcategory(match)) return getCategoryParentId(match);
  return article.category_id;
};

export const getArticleSubcategoryId = (article) => {
  if (article?.subcategory_id != null) return article.subcategory_id;
  return firstAssignedCategory(article, isSubcategory)?.id ?? null;
};

export const getArticleCategoryName = (article) => {
  const parent = firstAssignedCategory(article, (item) => !isSubcategory(item));
  if (parent?.name) return parent.name;
  return firstAssignedCategory(article, isSubcategory)?.parent?.name || "";
};

export const getArticleSubcategoryName = (article) =>
  firstAssignedCategory(article, isSubcategory)?.name || article?.subcategory?.name || "";

const wrapTextContent = (content = "") => {
  const value = String(content);
  if (!value.trim()) return "<p></p>";
  if (/^\s*</.test(value)) return value;
  return `<p>${value}</p>`;
};

const unwrapTextContent = (content = "") => {
  const value = String(content);
  const trimmed = value.trim();
  const match = trimmed.match(/^<p>([\s\S]*)<\/p>$/i);
  if (match && !/<\/?p[\s>]/i.test(match[1])) return match[1];
  return value;
};

export const newlinesToBreaks = (content = "") => String(content ?? "").replace(/\r\n|\r|\n/g, "<br>");

export const breaksToNewlines = (content = "") => String(content ?? "").replace(/<br\s*\/?>/gi, "\n");

const normalizeEditableText = (content = "") => breaksToNewlines(unwrapTextContent(content));

const asObject = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});

export const ARTICLE_LOCALES = [
  { value: "english", label: "English" },
  { value: "chinese", label: "中文" },
];

export const BILINGUAL_BLOCK_TYPES = ["heading", "paragraph", "quote", "button", "columns", "html"];
export const HEADING_LEVELS = ["h1", "h2", "h3", "h4", "h5"];

const padTableRow = (row = [], columnCount) => {
  const next = Array.isArray(row) ? [...row] : [];
  while (next.length < columnCount) next.push("");
  return next.slice(0, columnCount).map((cell) => String(cell ?? ""));
};

export const parseTableHtml = (html = "") => {
  const source = String(html || "").trim();
  const fallback = {
    headers: ["", ""],
    rows: [
      ["", ""],
      ["", ""],
    ],
  };
  if (!source || typeof DOMParser === "undefined") return fallback;

  const table = new DOMParser().parseFromString(source, "text/html").querySelector("table");
  if (!table) return fallback;

  const headerCells = [...table.querySelectorAll("thead th, thead td")];
  const bodyRows = [...table.querySelectorAll("tbody tr")].map((row) =>
    [...row.querySelectorAll("th, td")].map((cell) => cell.innerHTML.trim())
  );
  const allRows = [...table.querySelectorAll("tr")].map((row) =>
    [...row.querySelectorAll("th, td")].map((cell) => cell.innerHTML.trim())
  );

  let headers = headerCells.map((cell) => cell.innerHTML.trim());
  let rows = bodyRows;
  if (!headers.length && allRows.length) {
    headers = allRows[0];
    rows = allRows.slice(1);
  }

  const columnCount = Math.max(headers.length, ...rows.map((row) => row.length), 1);
  return {
    headers: padTableRow(headers.length ? headers : Array(columnCount).fill(""), columnCount),
    rows: (rows.length ? rows : [Array(columnCount).fill("")]).map((row) => padTableRow(row, columnCount)),
  };
};

export const serializeTableHtml = ({ headers = [], rows = [] } = {}) => {
  const columnCount = Math.max(headers.length, ...rows.map((row) => row.length), 1);
  const allRows = [
    padTableRow(headers.length ? headers : Array(columnCount).fill(""), columnCount),
    ...(rows.length ? rows : []).map((row) => padTableRow(row, columnCount)),
  ];
  const body = allRows
    .map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`)
    .join("");
  return `<table><tbody>${body}</tbody></table>`;
};

export const isBilingualBlockType = (type) => BILINGUAL_BLOCK_TYPES.includes(type);

export const createLocaleContent = (value = {}, extras = {}) => {
  const source = asObject(value);
  const result = {
    content: source.content ?? "",
    style: asObject(source.style),
  };
  Object.entries(extras).forEach(([key, fallback]) => {
    result[key] = source[key] ?? fallback;
  });
  return result;
};

export const getLocaleContent = (data = {}, locale = "english", { fallback = false } = {}) => {
  const current = asObject(data?.[locale]);
  if (!fallback || String(current.content || "").trim()) {
    return current;
  }
  return asObject(data?.english);
};

const localeExtrasForType = (type) => {
  if (type === "quote") return { author: "" };
  if (type === "button") return { url: "#" };
  return {};
};

const makeBlockId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID?.()) ||
  `block-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const bilingualPair = (data, extras = {}, legacyContent = "") => {
  const source = asObject(data);
  if (source.english || source.chinese) {
    return {
      english: createLocaleContent(source.english, extras),
      chinese: createLocaleContent(source.chinese, extras),
    };
  }
  return {
    english: createLocaleContent({ ...extras, content: legacyContent }, extras),
    chinese: createLocaleContent(extras, extras),
  };
};

export function defaultBlockData(type) {
  switch (type) {
    case "heading":
      return { level: "h2", ...bilingualPair({}, {}) };
    case "paragraph":
    case "text":
      return bilingualPair({}, {});
    case "image":
      return { url: "", alt: "", caption: "" };
    case "quote":
      return bilingualPair({}, { author: "" });
    case "button":
      return {
        target: "_self",
        ...bilingualPair(
          {
            english: { content: "Read More", url: "#" },
            chinese: { content: "阅读更多", url: "#" },
          },
          { url: "#" }
        ),
      };
    case "html":
      return bilingualPair({}, {});
    case "table":
      return {
        content: serializeTableHtml({
          headers: ["", ""],
          rows: [
            ["", ""],
            ["", ""],
          ],
        }),
        style: {},
      };
    case "video":
      return { url: "" };
    case "embed":
      return { embed_code: "" };
    case "columns":
      return {
        ...bilingualPair({}, {}),
        columns: [
          { blocks: [createClientBlock("paragraph")] },
          { blocks: [createClientBlock("paragraph")] },
        ],
      };
    default:
      return {};
  }
}

export function createClientBlock(type) {
  const resolved = type === "text" ? "paragraph" : type;
  return {
    id: makeBlockId(),
    type: resolved,
    data: defaultBlockData(resolved),
    status: true,
  };
}

const serializeLocale = (locale, extras = {}, { wrap = false, lineBreaks = false } = {}) => {
  const source = asObject(locale);
  let content = source.content ?? "";
  if (lineBreaks) content = newlinesToBreaks(content);
  const result = {
    content: wrap ? wrapTextContent(content) : content,
    style: asObject(source.style),
  };
  Object.keys(extras).forEach((key) => {
    result[key] = source[key] ?? extras[key];
  });
  return result;
};

const serializeBlockData = (type, data = {}) => {
  const source = asObject(data);
  const extras = localeExtrasForType(type);

  if (type === "heading") {
    return {
      level: HEADING_LEVELS.includes(source.level) ? source.level : "h2",
      english: serializeLocale(source.english, extras, { lineBreaks: true }),
      chinese: serializeLocale(source.chinese, extras, { lineBreaks: true }),
    };
  }

  if (type === "paragraph") {
    return {
      english: serializeLocale(source.english, extras, { wrap: true, lineBreaks: true }),
      chinese: serializeLocale(source.chinese, extras, { wrap: true, lineBreaks: true }),
    };
  }

  if (type === "quote" || type === "html" || type === "button") {
    const payload = {
      english: serializeLocale(source.english, extras, { lineBreaks: type === "quote" }),
      chinese: serializeLocale(source.chinese, extras, { lineBreaks: type === "quote" }),
    };
    if (type === "button") payload.target = source.target || "_self";
    return payload;
  }

  if (type === "columns") {
    return {
      english: serializeLocale(source.english, extras),
      chinese: serializeLocale(source.chinese, extras),
      columns: (source.columns || []).map((column) => ({
        ...column,
        blocks: (column.blocks || []).map(serializeNestedBlock),
      })),
    };
  }

  if (type === "image") {
    return serializeImageData(source);
  }

  if (type === "table") {
    return {
      content: source.content || serializeTableHtml(parseTableHtml(source.content)),
      style: asObject(source.style),
    };
  }

  return source;
};

const serializeNestedBlock = (block) => {
  const type = block?.type === "text" ? "paragraph" : block?.type;
  const payload = {
    type,
    data: serializeBlockData(type, block?.data || {}),
  };
  const nestedId = block?.database_id || (typeof block?.id === "number" ? block.id : null);
  if (nestedId) payload.id = nestedId;
  if (block?.status !== undefined) payload.status = block.status !== false;
  return payload;
};

export const serializeArticleBlocks = (blocks = []) =>
  blocks
    .filter((block) => block.type !== "gallery")
    .map((block) => {
    const type = block.type === "text" ? "paragraph" : block.type;
    const payload = {
      type,
      data: serializeBlockData(type, block.data || {}),
      status: block.status !== false,
    };
    if (block.database_id) payload.id = block.database_id;
    return payload;
  });

const normalizeBilingualData = (type, data = {}) => {
  const source = asObject(data);
  const extras = localeExtrasForType(type);
  let legacyContent = "";

  if (type === "heading") legacyContent = source.text ?? source.content ?? "";
  else if (type === "html") legacyContent = source.html ?? source.content ?? "";
  else if (type === "button") legacyContent = source.text ?? source.content ?? "";
  else legacyContent = source.content ?? "";

  if (type === "button" && !source.english && !source.chinese) {
    extras.url = source.url ?? "#";
  }
  if (type === "quote" && !source.english && !source.chinese) {
    extras.author = source.author ?? "";
  }

  const pair = bilingualPair(source, extras, legacyContent);

  if (type === "paragraph") {
    pair.english.content = normalizeEditableText(pair.english.content);
    pair.chinese.content = normalizeEditableText(pair.chinese.content);
  }

  if (type === "heading" || type === "quote") {
    pair.english.content = breaksToNewlines(pair.english.content);
    pair.chinese.content = breaksToNewlines(pair.chinese.content);
  }

  return pair;
};

export const normalizeBlockData = (type, data = {}) => {
  const source = asObject(data);
  const resolved = type === "text" ? "paragraph" : type;

  if (resolved === "heading") {
    return {
      level: HEADING_LEVELS.includes(source.level) ? source.level : "h2",
      ...normalizeBilingualData(resolved, source),
    };
  }

  if (resolved === "paragraph" || resolved === "quote" || resolved === "html") {
    return normalizeBilingualData(resolved, source);
  }

  if (resolved === "button") {
    return {
      target: source.target || "_self",
      ...normalizeBilingualData(resolved, source),
    };
  }

  if (resolved === "columns") {
    return {
      ...normalizeBilingualData(resolved, source),
      columns: (source.columns || []).map((column) => ({
        ...column,
        blocks: (column.blocks || []).map((nested) => {
          const nestedType = nested?.type === "text" ? "paragraph" : nested?.type;
          const nestedId = nested.id || nested.database_id || makeBlockId();
          return {
            ...nested,
            id: nestedId,
            database_id: nested.database_id || (typeof nested.id === "number" ? nested.id : undefined),
            type: nestedType,
            data: normalizeBlockData(nestedType, nested?.data || {}),
          };
        }),
      })),
    };
  }

  if (resolved === "image") {
    return serializeImageData(source);
  }

  if (resolved === "table") {
    return {
      content: source.content || serializeTableHtml(parseTableHtml("")),
      style: asObject(source.style),
    };
  }

  return source;
};

export const normalizeArticleBlock = (block) => {
  const type = block?.type === "text" ? "paragraph" : block?.type;
  return {
    ...block,
    type,
    data: normalizeBlockData(type, block?.data || {}),
  };
};

export const getArticleBlocks = (articleId) => apiRequest(`/articles/${articleId}/blocks`);
export const saveArticleBlocks = async (articleId, blocks = []) =>
  data(await apiRequest(`/articles/${articleId}/blocks`, json("PUT", { blocks: serializeArticleBlocks(blocks) })));

export const getMediaUrl = (item) =>
  item?.url || item?.file_url || item?.path || item?.file_path || "";

export const getMediaFilePath = (item) =>
  item?.file_path || item?.path || item?.file_url || item?.url || "";

export const serializeImageData = (data = {}) => ({
  url: data.url || data.file_path || "",
  alt: data.alt || data.alt_text || "",
  caption: data.caption || "",
});

export const mapMediaToImageData = (media = {}) => ({
  url: getMediaFilePath(media),
  alt: media.alt_text || media.alt || "",
  caption: media.caption || "",
  preview_url: getMediaUrl(media) || getMediaFilePath(media),
});

export const isImageMedia = (item) => {
  const type = String(item?.mime_type || "").toLowerCase();
  const url = getMediaUrl(item);
  return type.startsWith("image/") || /\.(jpe?g|png|webp|gif)$/i.test(url);
};

export const getFeaturedMediaId = (article) =>
  article?.featured_media_id ?? article?.featured_media?.id ?? null;

export const getFeaturedMediaUrl = (article) =>
  getMediaUrl(article?.featured_media) || article?.featured_image || "";

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const safeFileName = (name, fallback = "file") =>
  String(name || fallback).replace(/[^\w.-]+/g, "_") || fallback;

export const validateArticleMediaFile = (file, { allowPdf = false } = {}) => {
  if (!file || !file.size) return "Choose a file to upload.";
  if (file.size > MAX_UPLOAD_BYTES) {
    return "Each file must be 5 MB or smaller. Larger files are rejected by the server as a failed upload.";
  }
  const allowed = allowPdf ? [...IMAGE_TYPES, "application/pdf"] : IMAGE_TYPES;
  if (file.type && !allowed.includes(file.type)) {
    return allowPdf
      ? "Use JPG, PNG, WebP or PDF."
      : "Use JPG, PNG or WebP images only.";
  }
  return "";
};

export const getArticleMediaUploadError = (error) => {
  const messages = error?.payload?.errors
    ? Object.values(error.payload.errors).flat()
    : [];
  const message = messages.find(Boolean) || error?.message || "";
  if (/failed to upload/i.test(message)) {
    return "The file failed to upload. Use JPG, PNG or WebP (or PDF in the media library) and keep it under 5 MB.";
  }
  return "";
};

export const getArticleMedia = (params={}) => apiRequest(`/article-media${query(params)}`);
export const uploadArticleMedia = async({file,article_id=null,alt_text='',caption=''}) => {
  if (!file || !file.size) {
    throw new Error("Choose a JPG, PNG, WebP or PDF file to upload.");
  }
  const form = new FormData();
  form.append("file", file, safeFileName(file.name, "file"));
  if(article_id) form.append('article_id',article_id); form.append('alt_text',alt_text); form.append('caption',caption);
  return data(await apiRequest('/article-media',{method:'POST',body:form}));
};
export const deleteArticleMedia = (id) => apiRequest(`/article-media/${id}`,{method:'DELETE'});
